import { BusinessObjectBase } from './base';
import { resourceCategorySchema } from '@schemas/business-objects/resource-category';
import type { ResourceCategoryItem } from './resource-category-item';
import type { ResourceSet } from './resource-set';
import type { Id } from '../base-types';
import type { Resource } from './resource';
import type { TextBox } from './text-box';
import type { User } from './user';

export interface ResourceCategoryData {
  readonly id: Id;
  readonly name?: string;
  readonly passCriteria?: string;
  readonly sequence?: number;
  readonly items?: ResourceCategoryItem[];
  readonly resourceSetId: Id;
  readonly resourceSet?: ResourceSet;
  readonly locked?: boolean;
}

export interface ResourceCategory extends Readonly<ResourceCategoryData> {}
export class ResourceCategory extends BusinessObjectBase {
  static readonly entityName = 'ResourceCategory' as const;
  static readonly plural = 'resource-categories';
  static schema = resourceCategorySchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ResourceCategory[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ResourceCategory(data));
  }

  static async findOne(filter?: any): Promise<ResourceCategory | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ResourceCategory(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ResourceCategory> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ResourceCategory(data);
  }

  static async create(
    data: Omit<ResourceCategoryData, 'id'>,
  ): Promise<ResourceCategory> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ResourceCategory(result);
  }

  static async createMany(
    data: Omit<ResourceCategoryData, 'id'>[],
  ): Promise<ResourceCategory[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ResourceCategory(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ResourceCategoryData>,
  ): Promise<ResourceCategory> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ResourceCategory(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async dataForCopy(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ResourceCategory.plural}/${(this as any).id}/data-for-copy`;
    return BusinessObjectBase.post<any>(url, {});
  }
}
